import { IonContent, IonPage, useIonRouter } from "@ionic/react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";

import { ActionBar } from "../components/ActionBar";
import { Button } from "../components/Button";
import { Chip } from "../components/Chip";
import { CustomFoodSheet } from "../components/CustomFoodSheet";
import { Hero } from "../components/Hero";
import { I } from "../components/icons";
import { Notice } from "../components/Notice";
import {
  DaySplitView,
  QuestionFollowUp,
  QuestionHead,
  QuestionOptions,
  QuestionProgress,
  QuestionSearch,
  type QuestionOption,
} from "../components/Question";
import { NavRow, RowGroup } from "../components/Rows";
import { Segmented } from "../components/Segmented";
import { Sheet } from "../components/Sheet";
import { Screen } from "../components/Shell";
import { TopBar } from "../components/TopBar";
import { age } from "../domain/body";
import { customFoodByCode, foodTable } from "../domain/customFoods";
import { KCAL_PER_KG_FAT } from "../domain/deficit";
import { dayLabel } from "../domain/date";
import { GLP1_PROTEIN_PER_KG, deficitRule, older, proteinRule, type DeviceAnswers } from "../domain/eating";
import {
  AVOID_OPTS,
  CHEWING_OPTS,
  CONDITION_OPTS,
  COOK_OPTS,
  CRAVING_OPTS,
  DISLIKE_CHIPS,
  EVENING_OPTS,
  FAST_OPTS,
  HYPO_OPTS,
  LUNCH_OPTS,
  MEALS_OPTS,
  MED_OPTS,
  PATTERN_OPTS,
  PREGNANT_OPTS,
  SLEEP_OPTS,
  TRIGGER_MODE_OPTS,
  TRIGGER_OPTS,
  WEEKDAYS,
  WINDOW_OPTS,
  WINDOW_STARTS,
  daySplit,
  eatingScreens,
  eatingSteps,
  familyCount,
  mealsRestricted,
  medNotes,
  pruneAnswers,
  readBack,
  resolveDislikes,
  ruledOutByPattern,
  ruledOutLine,
  startLabel,
  stepCount,
  whatChanges,
  windowLine,
  type NamedFood,
  type ScreenId,
} from "../domain/eatingQuestions";
import { curWeight } from "../domain/energy";
import { familiesMatching, familyByKey } from "../domain/foodTags";
import { deficitTarget, proteinTarget } from "../domain/intake";
import { dailyTargets } from "../domain/nutrition";
import { weightSeries } from "../domain/trend";
import type { Pronouns } from "../domain/family";
import type { EatingAnswers, EatingCondition, EatingProfile, FoodItem, KayaState } from "../domain/types";
import { foodByCode, loadFoods, orderMatches } from "../lib/foodDb";
import type { EatingSafety } from "../storage/local";
import { deviceAnswers, useKaya } from "../store/kayaStore";
import { setEatingProfile } from "../store/toggles";

type Screen_ = ScreenId | "start";

const SAFETY_OPTS: QuestionOption<EatingSafety>[] = [
  { value: "no", label: "No" },
  { value: "yes", label: "Yes" },
  { value: "unsure", label: "Rather not say" },
];

const NONE = "none";

/** The block without its merge stamps: what the screens edit. */
function answersOf(e: EatingProfile | undefined): EatingAnswers {
  if (!e) return {};
  const { at: _at, avoidAt: _a, avoidOff: _b, dislikesAt: _c, dislikesOff: _d, ...rest } = e;
  return rest;
}

/** Adds or removes `v` in a list whose "none" answer is exclusive. */
function toggle<T extends string>(list: readonly T[] | undefined, v: T): T[] {
  const cur = list ?? [];
  return cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
}

const n = (v: number) => v.toLocaleString();

/**
 * Someone else's answers, filled in on their behalf through a linked account (ADR-046). The
 * questions are the same; what differs is whose state they read, where the answers go, and that
 * the safety question is left for the person's own phone — it never leaves a phone, and this is
 * not theirs.
 */
export interface QuestionSubject {
  S: KayaState;
  /** what the page calls them ("Mum") */
  name: string;
  pron: Pronouns;
  /** where Back and a finished save go */
  back: string;
  /** writes the answers through the link; rejects when the server does */
  save: (answers: EatingAnswers) => Promise<void>;
}

/**
 * "Plan my food" — the questionnaire (plan §3.4; boards EatingStart … EatingReadBack; ADR-042).
 *
 * One question a screen at the large scale, tap-only, each option saying what it changes. The
 * answers live in a draft on this phone until the read-back's Save writes `profile.eating` in one
 * stamped block; closing midway keeps the draft. The safety answer never leaves the phone.
 */
export default function FoodQuestions() {
  return (
    <IonPage>
      <Questions />
    </IonPage>
  );
}

/**
 * The questionnaire inside its page — this phone's own, or with `subject` someone else's through
 * a link. Apart from the page so the family view can keep one `IonPage` while their state loads.
 */
export function Questions({ subject }: { subject?: QuestionSubject }) {
  const own = useKaya((s) => s.S);
  const S = subject?.S ?? own;
  const meta = useKaya((s) => s.meta);
  const update = useKaya((s) => s.update);
  const showToast = useKaya((s) => s.showToast);
  const setEatingDraft = useKaya((s) => s.setEatingDraft);
  const setEatingSafety = useKaya((s) => s.setEatingSafety);
  const router = useIonRouter();
  const location = useLocation();
  const from = subject?.back ?? (new URLSearchParams(location.search).get("from") === "you" ? "/you/food" : "/food");
  const today = new Date();

  // A draft is resumed only while nothing has been saved since it began, on this phone or another.
  const init = useMemo(() => {
    const d = subject ? undefined : meta.eatingDraft;
    const savedAt = S.profile.eating?.at;
    if (d && d.since === savedAt) return d;
    return {
      answers: answersOf(S.profile.eating),
      safety: subject ? undefined : meta.eatingSafety,
      step: "start",
      since: savedAt,
    };
    // the draft is read once, when the questionnaire opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [a, setA] = useState<EatingAnswers>(init.answers);
  const [safety, setSafety] = useState<EatingSafety | undefined>(init.safety);
  const [screen, setScreen] = useState<Screen_>(init.step as Screen_);
  const [reading, setReading] = useState(false);
  const [q, setQ] = useState("");
  const [customFor, setCustomFor] = useState<string | null>(null);
  const [foods, setFoods] = useState<FoodItem[] | null>(null);
  const [weekdayOpen, setWeekdayOpen] = useState(false);
  const content = useRef<HTMLIonContentElement>(null);

  useEffect(() => {
    loadFoods()
      .then((f) => setFoods(f))
      .catch(() => setFoods([]));
  }, []);

  // Every change is kept on this phone, so closing midway loses nothing. Not someone else's: the
  // draft slot is this phone's own questionnaire.
  useEffect(() => {
    if (!subject) setEatingDraft({ answers: a, safety, step: screen, since: init.since });
  }, [a, safety, screen, init.since, setEatingDraft, subject]);

  const set = (patch: Partial<EatingAnswers>) => setA((cur) => ({ ...cur, ...patch }));

  const screens = eatingScreens(S, a, today).filter((s) => !subject || s !== "safety");
  // a screen that stopped applying (diabetes unticked while on the medicines) falls back to the list
  const cur: Screen_ = screen === "start" || screens.includes(screen) ? screen : screens[0];
  const idx = cur === "start" ? -1 : screens.indexOf(cur);

  const lookup = (id: string): NamedFood | null => {
    const f = customFoodByCode(S, id) ?? foodByCode(id);
    return f ? { i: f.i, n: f.n } : null;
  };
  const dislikes = resolveDislikes(a.dislikes ?? [], lookup);
  // Missing ids are only known once the table is in; before that an id may just be unloaded.
  const missing = foods ? dislikes.missing : [];

  const restricted = mealsRestricted(S, a, today);
  // Someone else's budget never reads this phone's safety answer; theirs stays on their phone.
  const before: DeviceAnswers = subject ? {} : deviceAnswers(meta.eatingSafety);
  const device: DeviceAnswers = subject ? {} : deviceAnswers(safety);
  const budget = dailyTargets(S, today, undefined, before).kcal;

  const go = (s: Screen_) => {
    setScreen(s);
    setQ("");
    void content.current?.scrollToTop(0);
  };
  const next = () => {
    if (cur === "dislikes" && a.dislikes === undefined) set({ dislikes: [] });
    if (cur === "sleep" && a.shift === undefined) set({ shift: false });
    if (cur === "safety" || idx === screens.length - 1) {
      setReading(true);
      return;
    }
    go(screens[idx + 1]);
  };
  const back = () => go(idx <= 0 ? "start" : screens[idx - 1]);

  const save = () => {
    const keep = pruneAnswers(S, a, today);
    if (missing.length) keep.dislikes = (keep.dislikes ?? []).filter((k) => !missing.includes(k));
    if (subject) {
      subject.save(keep).then(
        () => {
          setReading(false);
          router.push(from, "back");
        },
        (e: unknown) => showToast(e instanceof Error ? e.message : "Could not save — try again")
      );
      return;
    }
    update((d) => {
      setEatingProfile(d, keep);
    });
    setEatingSafety(safety);
    setEatingDraft(undefined);
    setReading(false);
    showToast("Answers saved");
    router.push(from, "back");
  };

  const answered = (s: Screen_): boolean => {
    switch (s) {
      case "pregnant":
        return !!a.pregnant;
      case "pattern":
        return !!a.pattern;
      case "avoid":
        return a.avoid !== undefined;
      case "dislikes":
        return true;
      case "conditions":
        return (
          (a.conditions !== undefined || !!a.conditionsPrivate) &&
          (!(a.conditions ?? []).includes("diabetes") || !!a.hypoAnswer)
        );
      case "meds":
        return a.meds !== undefined;
      case "chewing":
        return !!a.chewing;
      case "meals":
        return !!a.meals && (a.meals !== "2" || !!restricted || !!a.evening);
      case "window":
        return !!a.window;
      case "cook":
        return !!a.cook;
      case "lunch":
        return !!a.lunch;
      case "fasts":
        return a.fasts !== undefined && !(weekdayOpen && !a.fasts.some((f) => WEEKDAYS.some((w) => w.value === f)));
      case "triggers":
        return a.triggers !== undefined && (!a.triggers.length || !!a.triggerMode);
      case "cravings":
        return !!a.cravingTime;
      case "sleep":
        return !!a.sleep;
      case "safety":
        return !!safety;
      default:
        return true;
    }
  };

  // Kit §28: a disabled button says why. The screen's own answer, or the follow-up it opened.
  const reasonFor = (s: Screen_): string => {
    if (s === "fasts" && weekdayOpen) return "Pick the day";
    if (
      (s === "meals" && a.meals === "2") ||
      (s === "triggers" && !!a.triggers?.length) ||
      (s === "conditions" && (a.conditions ?? []).includes("diabetes"))
    )
      return "Answer the follow-up too";
    return "Pick an answer";
  };

  const count = (id: Parameters<typeof stepCount>[2]) => stepCount(S, a, id, today);
  const progress = (id: ScreenId) => {
    const c = id === "safety" ? null : count(id);
    return c ? <QuestionProgress n={c.n} of={c.of} /> : <QuestionProgress label="Last question · optional" />;
  };
  const countWords = (id: Parameters<typeof stepCount>[2]) => {
    const c = count(id);
    return c ? `Question ${c.n} of ${c.of}` : undefined;
  };

  const head = (
    <TopBar
      title={subject ? `${subject.name}’s answers` : "Plan my food"}
      back={
        subject
          ? { to: from, label: `Back to ${subject.name}` }
          : cur === "start"
          ? { to: from, label: from === "/food" ? "Back to Food" : "Back to Food & calories" }
          : { to: from, label: `Close, back to ${from === "/food" ? "Food" : "Food & calories"}. Answers so far are kept`, close: true }
      }
    />
  );

  /* ---------- the screens ---------- */

  const body = (): ReactNode => {
    switch (cur) {
      case "start":
        return renderStart(eatingSteps(S, a, today).length);

      case "pregnant":
        return (
          <Single
            id="q-preg"
            title="Are you pregnant or breastfeeding?"
            options={PREGNANT_OPTS}
            value={a.pregnant}
            onPick={(v) => set({ pregnant: v })}
            foot="Either one sets the deficit to 0 — the body needs more, not less."
          />
        );

      case "pattern":
        return (
          <Single
            id="q-pattern"
            title="How do you eat?"
            options={PATTERN_OPTS}
            value={a.pattern}
            onPick={(v) => set({ pattern: v })}
            foot="The plan only proposes foods that fit. You can still log anything."
          />
        );

      case "avoid": {
        const opts: QuestionOption<string>[] = [...AVOID_OPTS, { value: NONE, label: "None" }];
        const chosen = a.avoid === undefined ? [] : a.avoid.length ? a.avoid : [NONE];
        return (
          <div className="k-q__block">
            <QuestionHead id="q-avoid" title="Anything you must never eat?" help="Pick all that apply." />
            <QuestionOptions
              labelledBy="q-avoid"
              options={opts}
              chosen={chosen}
              onPick={(v) =>
                set({
                  avoid:
                    v === NONE
                      ? []
                      : toggle(a.avoid, v as (typeof AVOID_OPTS)[number]["value"]),
                })
              }
            />
            <p className="k-qfine">Anything ticked here is never planned and never offered as a swap.</p>
          </div>
        );
      }

      case "dislikes":
        return (
          <Dislikes
            keys={a.dislikes}
            pattern={a.pattern}
            shown={dislikes.shown}
            missing={missing}
            foods={foods}
            q={q}
            setQ={setQ}
            onChange={(keys) => set({ dislikes: keys })}
            S={S}
            // their own foods are theirs to add, on their phone
            onCustom={subject ? undefined : (name) => setCustomFor(name)}
          />
        );

      case "conditions": {
        const opts: QuestionOption<string>[] = [
          ...CONDITION_OPTS,
          { value: NONE, label: "None" },
          { value: "private", label: "Rather not say" },
        ];
        const chosen = a.conditionsPrivate
          ? ["private"]
          : a.conditions === undefined
            ? []
            : a.conditions.length
              ? a.conditions
              : [NONE];
        const pick = (v: string) => {
          if (v === "private") return set({ conditions: [], conditionsPrivate: true, hypoAnswer: undefined, hypoRisk: undefined });
          if (v === NONE) return set({ conditions: [], conditionsPrivate: undefined, hypoAnswer: undefined, hypoRisk: undefined });
          const next = toggle(a.conditions, v as EatingCondition);
          set({
            conditions: next,
            conditionsPrivate: undefined,
            ...(next.includes("diabetes") ? {} : { hypoAnswer: undefined, hypoRisk: undefined }),
          });
        };
        return (
          <div className="k-q__block">
            <QuestionHead id="q-cond" title="Any of these?" help="Pick all that apply." />
            <QuestionOptions
              labelledBy="q-cond"
              options={opts}
              chosen={chosen}
              onPick={pick}
              follow={(v) =>
                v === "diabetes" ? (
                  <QuestionFollowUp
                    count={countWords("hypo")}
                    question="Do you take insulin, or tablets your doctor said can cause low sugar?"
                    line="Yes or Not sure: no skipped meals, no deficit on fast days, and some carbohydrate in every meal."
                  >
                    <Segmented
                      ariaLabel="Insulin or low-sugar tablets"
                      options={HYPO_OPTS}
                      value={a.hypoAnswer ?? null}
                      onChange={(h) => set({ hypoAnswer: h, hypoRisk: h !== "no" })}
                    />
                  </QuestionFollowUp>
                ) : null
              }
            />
          </div>
        );
      }

      case "meds": {
        const opts: QuestionOption<string>[] = [...MED_OPTS, { value: NONE, label: "None" }];
        const chosen = a.meds === undefined ? [] : a.meds.length ? a.meds : [NONE];
        const on = a.meds ?? [];
        const glpG = Math.round(Math.max(GLP1_PROTEIN_PER_KG, proteinRule(S, today).perKg) * curWeight(S));
        return (
          <div className="k-q__block">
            <QuestionHead id="q-meds" title="Do you take any of these?" help="Pick all that apply." />
            <QuestionOptions
              labelledBy="q-meds"
              options={opts}
              chosen={chosen}
              onPick={(v) =>
                set({ meds: v === NONE ? [] : toggle(a.meds, v as (typeof MED_OPTS)[number]["value"]) })
              }
            />
            <RowGroup heading="What Kaya will do with this">
              {medNotes(curWeight(S) > 0 ? glpG : null).map((m) => {
                const ticked = on.includes(m.med);
                return (
                  <div key={m.med} className={`k-row k-qnote${ticked ? "" : " is-off"}`}>
                    <span className="k-row__tile" aria-hidden="true">
                      {ticked ? I.tick : I.minus}
                    </span>
                    <span className="k-row__txt">
                      <span className="k-row__title">{m.title}</span>
                      {(ticked ? m.on : m.off) ? <span className="k-row__sub">{ticked ? m.on : m.off}</span> : null}
                    </span>
                  </div>
                );
              })}
            </RowGroup>
            <p className="k-qfine">
              {on.includes("glp1")
                ? "The medicine’s protein floor and deficit cap change your targets; the rest are notes."
                : "Notes only — your targets do not change."}
            </p>
          </div>
        );
      }

      case "chewing":
        return (
          <Single
            id="q-chew"
            title="Any trouble chewing hard foods?"
            options={CHEWING_OPTS}
            value={a.chewing}
            onPick={(v) => set({ chewing: v })}
          />
        );

      case "meals": {
        const why =
          restricted === "60+"
            ? "After 60 Kaya plans three meals and a snack: a long gap between meals costs muscle, and protein keeps better spread across the day."
            : "On those tablets a long gap can bring sugar too low, so Kaya plans three meals and a snack.";
        // a two-meal day waits for its evening: "light" and "full" are different days
        const split = a.meals && budget !== null && (a.meals !== "2" || a.evening || restricted) ? daySplit(a.meals, a.evening, { older: older(S, today), restricted: !!restricted }, budget) : null;
        const planned = split ? split.rows.reduce((t, r) => t + r.kcal, 0) : 0;
        return (
          <div className="k-q__block">
            <QuestionHead id="q-meals" title="Meals in a day?" />
            <QuestionOptions
              labelledBy="q-meals"
              single
              options={MEALS_OPTS}
              chosen={a.meals ? [a.meals] : []}
              onPick={(v) => set({ meals: v })}
              follow={(v) =>
                (v === "2" || v === "1") && restricted ? (
                  <QuestionFollowUp question="Why Kaya plans more" line={why} />
                ) : v === "2" ? (
                  <QuestionFollowUp
                    count={countWords("meals")}
                    question="Your evening meal is usually…"
                    line={
                      a.evening === "full"
                        ? `Full: two meals of about ${budget === null ? "half your budget" : `~${n(Math.round(budget / 2 / 10) * 10)} kcal`} each; the morning one carries half your protein.`
                        : a.evening === "varies"
                          ? "Varies: Kaya plans a light evening and shows what a full one would add."
                          : a.evening === "light"
                            ? `Light: seeds, nuts, fruit or curd, ~350 kcal. The morning meal carries the day${split ? ` — ~${n(split.rows[0].kcal)} kcal and most of your protein` : ""}; Kaya says when protein cannot fit.`
                            : undefined
                    }
                  >
                    <Segmented
                      ariaLabel="Your evening meal"
                      options={EVENING_OPTS}
                      value={a.evening ?? null}
                      onChange={(e) => set({ evening: e })}
                    />
                  </QuestionFollowUp>
                ) : null
              }
            />
            {split ? (
              <DaySplitView
                rows={split.rows}
                unplanned={split.unplanned}
                note={
                  split.unplanned && budget !== null
                    ? `~${n(planned)} of your ~${n(budget)} est. budget — a light evening leaves ~${n(split.unplanned)} unplanned; fine on a rest day.`
                    : `Of your ~${n(budget ?? 0)} kcal est. budget · shaped on ICMR-NIN’s sample days`
                }
              />
            ) : a.meals && budget === null ? (
              <p className="k-qfine">Add your year of birth and sex under Body & goals to see how the day is split.</p>
            ) : null}
          </div>
        );
      }

      case "window": {
        const why =
          restricted === "60+"
            ? "After 60 Kaya plans meals across the day instead, and says why on the plan."
            : "On low-sugar tablets Kaya plans meals across the day instead, and says why on the plan.";
        return (
          <div className="k-q__block">
            <QuestionHead
              id="q-window"
              title="Do you keep an eating window?"
              help="Intermittent fasting, if you do it. Meals are planned inside the window."
            />
            <QuestionOptions
              labelledBy="q-window"
              single
              options={WINDOW_OPTS}
              chosen={a.window ? [a.window] : []}
              onPick={(v) =>
                set({
                  window: v,
                  windowStart: v === "16:8" || v === "14:10" ? (a.window === v ? a.windowStart : WINDOW_STARTS[v].usual) : undefined,
                })
              }
              follow={(v) =>
                v === "16:8" || v === "14:10" ? (
                  restricted ? (
                    <QuestionFollowUp question="Why Kaya plans across the day" line={why} />
                  ) : (
                    <QuestionFollowUp
                      count={countWords("window")}
                      question="It usually starts about…"
                      line={windowLine(a.meals ?? "3s", v, a.windowStart ?? WINDOW_STARTS[v].usual)}
                    >
                      <Segmented
                        ariaLabel="When the window starts"
                        options={WINDOW_STARTS[v].starts.map((h) => ({ value: h, label: startLabel(h) }))}
                        value={a.windowStart ?? WINDOW_STARTS[v].usual}
                        onChange={(h) => set({ windowStart: h })}
                      />
                    </QuestionFollowUp>
                  )
                ) : v === "sunset" && restricted ? (
                  <QuestionFollowUp question="Why Kaya plans across the day" line={why} />
                ) : null
              }
            />
            <p className="k-qfine">At 60+, or on low-sugar tablets, Kaya plans meals across the day and says why.</p>
          </div>
        );
      }

      case "cook":
        return (
          <Single id="q-cook" title="Who cooks your meals?" options={COOK_OPTS} value={a.cook} onPick={(v) => set({ cook: v })} />
        );

      case "lunch":
        return (
          <Single
            id="q-lunch"
            title="Weekday lunch is usually…"
            options={LUNCH_OPTS}
            value={a.lunch}
            onPick={(v) => set({ lunch: v })}
          />
        );

      case "fasts": {
        const days = (a.fasts ?? []).filter((f) => WEEKDAYS.some((w) => w.value === f));
        const opts: QuestionOption<string>[] = [...FAST_OPTS, { value: NONE, label: "None" }];
        const wk = weekdayOpen || days.length > 0;
        const chosen = [
          ...(a.fasts ?? []).filter((f) => !days.includes(f)),
          ...(wk ? ["weekday"] : []),
          ...(a.fasts !== undefined && !a.fasts.length && !wk ? [NONE] : []),
        ];
        const pick = (v: string) => {
          if (v === NONE) {
            setWeekdayOpen(false);
            return set({ fasts: [] });
          }
          if (v === "weekday") {
            if (wk) {
              setWeekdayOpen(false);
              return set({ fasts: (a.fasts ?? []).filter((f) => !days.includes(f)) });
            }
            setWeekdayOpen(true);
            return set({ fasts: a.fasts ?? [] });
          }
          set({ fasts: toggle(a.fasts, v) });
        };
        return (
          <div className="k-q__block">
            <QuestionHead id="q-fasts" title="Do you fast on any days?" help="Pick all that apply." />
            <QuestionOptions
              labelledBy="q-fasts"
              options={opts}
              chosen={chosen}
              onPick={pick}
              follow={(v) =>
                v === "weekday" ? (
                  <QuestionFollowUp count={countWords("fasts")} question="Which day?">
                    <div className="k-chipwrap" role="group" aria-label="Fast days">
                      {WEEKDAYS.map((w) => (
                        <Chip
                          key={w.value}
                          size="lg"
                          selected={days.includes(w.value)}
                          onClick={() => set({ fasts: toggle(a.fasts, w.value) })}
                        >
                          {w.label}
                        </Chip>
                      ))}
                    </div>
                  </QuestionFollowUp>
                ) : null
              }
            />
            <p className="k-qfine">A fast day uses fast-day foods and never stacks a deficit on top.</p>
          </div>
        );
      }

      case "triggers": {
        const t = a.triggers ?? [];
        return (
          <div className="k-q">
            <div className="k-q__block">
              <QuestionHead id="q-trig" title="Foods that are hard to stop once you start?" help="Pick all that apply." />
              <div className="k-chipwrap" role="group" aria-labelledby="q-trig">
                {TRIGGER_OPTS.map((o) => (
                  <Chip
                    key={o.value}
                    size="lg"
                    selected={t.includes(o.value)}
                    onClick={() => {
                      const next = toggle(a.triggers, o.value);
                      set({ triggers: next, triggerMode: next.length ? (a.triggerMode ?? "2x") : undefined });
                    }}
                  >
                    {o.label}
                  </Chip>
                ))}
                <Chip
                  size="lg"
                  selected={a.triggers !== undefined && !t.length}
                  onClick={() => set({ triggers: [], triggerMode: undefined })}
                >
                  None
                </Chip>
              </div>
            </div>
            {t.length ? (
              <div className="k-q__block">
                <QuestionHead id="q-treat" second title="How should your plan treat them?" />
                <QuestionOptions
                  labelledBy="q-treat"
                  single
                  options={TRIGGER_MODE_OPTS.map((o) =>
                    o.value === "2x" && !t.includes("sweets") ? { ...o, sub: "A small portion in a snack slot" } : o
                  )}
                  chosen={a.triggerMode ? [a.triggerMode] : []}
                  onPick={(v) => set({ triggerMode: v })}
                />
                <p className="k-qfine">A planned portion, never a ban — that is what the evidence favours.</p>
              </div>
            ) : null}
          </div>
        );
      }

      case "cravings":
        return (
          <Single
            id="q-crave"
            title="When do cravings usually hit?"
            help="Kaya puts one line on Today, just before that time."
            options={CRAVING_OPTS}
            value={a.cravingTime}
            onPick={(v) => set({ cravingTime: v })}
          />
        );

      case "sleep":
        return (
          <div className="k-q">
            <Single
              id="q-sleep"
              title="How much do you usually sleep?"
              options={SLEEP_OPTS}
              value={a.sleep}
              onPick={(v) => set({ sleep: v })}
            />
            <div className="k-q__block">
              <QuestionHead id="q-shift" second title="And shifts?" />
              <QuestionOptions
                labelledBy="q-shift"
                options={[{ value: "yes", label: "I work night or rotating shifts", sub: "Meals follow your waking hours, not the clock" }]}
                chosen={a.shift ? ["yes"] : []}
                onPick={() => set({ shift: !a.shift })}
              />
            </div>
          </div>
        );

      case "safety":
        return (
          <div className="k-q__block">
            <QuestionHead
              id="q-safety"
              title="In the last few months, have you often felt out of control around food, or made up for eating by vomiting, fasting or pills?"
              help="Not a diagnosis, and not a validated test. It only tells Kaya to go gentler."
            />
            <QuestionOptions
              labelledBy="q-safety"
              single
              options={SAFETY_OPTS}
              chosen={safety ? [safety] : []}
              onPick={(v) => setSafety(v)}
            />
            <Notice tone="info" icon={I.phone}>
              Yes switches the deficit off until you change it. This answer stays on this phone and never syncs.
            </Notice>
            {safety === "yes" ? (
              <p className="k-qfine">
                If food has felt like this for a while, a doctor or a counsellor can help — it is common, and it is treatable.
              </p>
            ) : null}
          </div>
        );
    }
  };

  const isStart = cur === "start";

  return (
    <IonContent ref={content}>
      <Screen>
        <div className="k-large k-q">
          {head}
          {!isStart ? progress(cur) : null}
          {body()}
        </div>

        <div className="k-large">
          {isStart ? (
            <ActionBar primary={{ label: "Start", onClick: () => go(screens[0]) }} />
          ) : (
            <ActionBar
              secondary={{ label: "Back", onClick: back }}
              also={cur === "safety" ? { label: "Skip", onClick: () => { setSafety(undefined); setReading(true); } } : undefined}
              primary={{ label: "Next", onClick: next, disabled: !answered(cur), reason: reasonFor(cur) }}
            />
          )}
        </div>
      </Screen>

      {reading ? (
        <ReadBack
          sentences={readBack(S, pruneAnswers(S, a, today), lookup, today)}
          changes={whatChanges(S, pruneAnswers(S, a, today), lookup, { before, after: device }, today)}
          title={subject ? `${subject.name}’s answers` : undefined}
          onClose={() => setReading(false)}
          onSave={save}
        />
      ) : null}

      {subject ? null : (
        <CustomFoodSheet
          open={customFor !== null}
          prefillName={customFor ?? undefined}
          onClose={() => setCustomFor(null)}
          onSaved={(id) => {
            set({ dislikes: [...(a.dislikes ?? []).filter((k) => k !== id), id] });
            setQ("");
          }}
        />
      )}
    </IonContent>
  );

  /* ---------- the start ---------- */

  function renderStart(steps: number) {
    const years = age(S, today);
    const ws = weightSeries(S);
    const last = ws.length ? ws[ws.length - 1] : null;
    const deficit = deficitTarget(S, today, before);
    const perKg = proteinRule(S, today).perKg;
    const sex = S.profile.sex === "female" ? "Female" : S.profile.sex === "male" ? "Male" : "–";
    const rule = deficitRule(S, today, before);
    return (
      <>
        {subject ? (
          <Notice tone="info" icon={I.user}>
            You are answering for {subject.name}. Read {subject.pron.them} each question; what you save reaches{" "}
            {subject.pron.their} phone the next time it syncs. The last question, about how food feels, is left for{" "}
            {subject.pron.their} own phone.
          </Notice>
        ) : null}
        <Hero
          label="About"
          figure={3}
          unit="minutes, all taps"
          line={`${steps} questions, one a screen. Nothing to type.`}
          visual={
            <ul className="k-qstart__lines">
              <li>
                {I.tick}The plan never proposes a food you don’t eat.
              </li>
              <li>
                {I.tick}Your targets follow your age and any condition you name.
              </li>
              <li>
                {I.tick}Change any answer later, under You › Food & calories.
              </li>
            </ul>
          }
        />
        <div>
          <RowGroup heading="Kaya already knows">
            <NavRow kind="value" icon={I.user} title="Sex" value={sex} />
            <NavRow
              kind="value"
              icon={I.calendar}
              title="Year of birth"
              value={S.profile.birthYear ? `${S.profile.birthYear}${years !== null ? ` · ${years}` : ""}` : "–"}
            />
            <NavRow kind="value" icon={I.tape} title="Height" value={S.profile.heightCm ? `${S.profile.heightCm} cm` : "–"} />
            <NavRow
              kind="value"
              icon={I.scale}
              title="Weight"
              subtitle={last ? dayLabel(last.date) : undefined}
              value={last ? `${last.v.toFixed(1)} kg` : `${curWeight(S).toFixed(1)} kg`}
            />
            <NavRow
              kind="value"
              icon={I.target}
              title="Pace"
              subtitle={deficit ? `About ${((deficit * 7) / KCAL_PER_KG_FAT).toFixed(2)} kg a week` : rule.limits.length ? "No deficit" : undefined}
              value={`${n(deficit)} kcal a day`}
            />
            <NavRow
              kind="value"
              icon={I.bowl}
              title="Protein"
              subtitle={`About ${n(proteinTarget(S, curWeight(S), today))} g a day`}
              value={`${perKg.toFixed(1)} g/kg`}
            />
          </RowGroup>
          <p className="k-qfine" style={{ margin: "8px 0 0 4px" }}>
            {subject ? (
              <>Not asked again. {subject.name} changes them on {subject.pron.their} phone, under You › Body & goals.</>
            ) : (
              <>
                Not asked again. Change them under <Link to="/you/body">Body & goals</Link>.
              </>
            )}
          </p>
        </div>
        {!S.profile.birthYear || (S.profile.sex !== "female" && S.profile.sex !== "male") ? (
          subject ? (
            <Notice tone="caution">
              Kaya needs {subject.name}’s year of birth and sex to set {subject.pron.their} targets. {subject.name} adds
              them on {subject.pron.their} phone; you can answer now.
            </Notice>
          ) : (
            <Notice tone="caution" primary={{ label: "Add them", onClick: () => router.push("/you/body", "forward") }}>
              Kaya needs your year of birth and sex to set your targets. Add them first, or answer now and add them after.
            </Notice>
          )
        ) : null}
        <Notice tone="info">
          This is food guidance, not medical advice. If a doctor or dietitian has given you a diet, follow theirs.
        </Notice>
      </>
    );
  }
}

/* ---------- one-answer screens ---------- */

function Single<T extends string>({
  id,
  title,
  help,
  options,
  value,
  onPick,
  foot,
}: {
  id: string;
  title: string;
  help?: string;
  options: readonly QuestionOption<T>[];
  value: T | undefined;
  onPick: (v: T) => void;
  foot?: string;
}) {
  return (
    <div className="k-q__block">
      <QuestionHead id={id} title={title} help={help} />
      <QuestionOptions labelledBy={id} single options={options} chosen={value ? [value] : []} onPick={onPick} />
      {foot ? <p className="k-qfine">{foot}</p> : null}
    </div>
  );
}

/* ---------- dislikes: the chips, the person's own, and the search ---------- */

function Dislikes({
  keys,
  pattern,
  shown,
  missing,
  foods,
  q,
  setQ,
  onChange,
  S,
  onCustom,
}: {
  keys: string[] | undefined;
  pattern: EatingAnswers["pattern"];
  shown: { key: string; label: string; kind: "family" | "food" }[];
  missing: string[];
  foods: FoodItem[] | null;
  q: string;
  setQ: (q: string) => void;
  onChange: (keys: string[]) => void;
  /** whose table of foods to search: their own foods are in it */
  S: KayaState;
  onCustom?: (name: string) => void;
}) {
  const list = keys ?? [];
  const out = ruledOutByPattern(pattern);
  const mine = shown.filter((d) => !DISLIKE_CHIPS.includes(d.key));
  const picked = shown.filter((d) => DISLIKE_CHIPS.includes(d.key));
  const add = (k: string) => onChange(list.includes(k) ? list : [...list, k]);
  const drop = (k: string) => onChange(list.filter((x) => x !== k));
  const searching = q.trim().length >= 2;
  const ruled = ruledOutLine(pattern);
  const firstFamily = mine.find((d) => d.kind === "family");

  // Name matches only, in the picker's order; the fuzzy tail would offer fried rice for "bhin".
  const table = useMemo(() => (foods ? foodTable(S, foods) : []), [S, foods]);
  const families = searching ? familiesMatching(q) : [];
  const dishes = searching ? orderMatches(table, q).slice(0, 8) : [];

  return (
    <div className="k-q__block">
      <QuestionHead id="q-dis" title="Foods you don’t like?" help={searching ? undefined : "Tap any you’d rather not see."} />
      {searching ? (
        <p className="k-qfine">
          {picked.length || mine.length
            ? `Picked: ${picked.map((d) => d.label).join(", ") || "none"}${mine.length ? ` · yours: ${mine.map((d) => d.label).join(", ")}` : ""}`
            : "Nothing picked yet"}
        </p>
      ) : (
        <>
          <div className="k-chipwrap" role="group" aria-labelledby="q-dis">
            {DISLIKE_CHIPS.map((k) => {
              const fam = familyByKey(k);
              if (!fam) return null;
              const off = out.includes(k);
              return (
                <Chip
                  key={k}
                  size="lg"
                  disabled={off}
                  selected={!off && list.includes(k)}
                  onClick={() => (list.includes(k) ? drop(k) : add(k))}
                >
                  {fam.label}
                </Chip>
              );
            })}
            <Chip size="lg" selected={keys !== undefined && !list.length} onClick={() => onChange([])}>
              None of these
            </Chip>
          </div>
          {mine.length ? (
            <div>
              <h2 id="q-dis-yours" className="k-group__h">
                Yours
              </h2>
              <div className="k-chipwrap" role="group" aria-labelledby="q-dis-yours">
                {mine.map((d) => (
                  <Chip key={d.key} size="lg" own ariaLabel={`Remove ${d.label.toLowerCase()} from your dislikes`} onClick={() => drop(d.key)}>
                    {d.label}
                  </Chip>
                ))}
              </div>
            </div>
          ) : null}
        </>
      )}

      {missing.length ? (
        <Notice tone="caution">
          {missing.length === 1 ? "A dish you picked is" : `${missing.length} dishes you picked are`} no longer in the food
          table, so {missing.length === 1 ? "it is" : "they are"} left out when you save.
        </Notice>
      ) : null}

      <QuestionSearch label="Something else you don’t like" value={q} onChange={setQ} placeholder="Add another — sabudana, arbi" />

      {searching ? (
        <>
          <div className="k-rows" role="list" aria-label="Matches">
            {families.map((f) => {
              const have = list.includes(f.key);
              const c = foods ? familyCount(f, foods) : null;
              return (
                <div key={f.key} role="listitem" className="k-qres">
                  <span className="k-row__txt">
                    <span className="k-row__title">{f.label} — every dish</span>
                    <span className="k-row__sub">
                      {c === null
                        ? "Keeps every one of them out"
                        : c
                          ? `${n(c)} dish${c === 1 ? "" : "es"} in the table · keeps all of them out`
                          : "None in the table yet · keeps out any you add yourself"}
                    </span>
                  </span>
                  <Button
                    variant="text"
                    ariaLabel={have ? `${f.label}, every dish, added` : `Add ${f.label.toLowerCase()}, every dish`}
                    disabled={have}
                    onClick={() => add(f.key)}
                  >
                    {have ? "Added" : "Add"}
                  </Button>
                </div>
              );
            })}
            {dishes.map((f) => {
              const have = list.includes(f.i);
              return (
                <div key={f.i} role="listitem" className="k-qres">
                  <span className="k-row__txt">
                    <span className="k-row__title">{f.n}</span>
                  </span>
                  <Button
                    variant="text"
                    ariaLabel={have ? `${f.n}, added` : `Add ${f.n} only`}
                    disabled={have}
                    onClick={() => add(f.i)}
                  >
                    {have ? "Added" : "Add"}
                  </Button>
                </div>
              );
            })}
            {onCustom ? (
              <div role="listitem" className="k-qres-custom">
                <button type="button" className="k-row k-row--link" onClick={() => onCustom(q.trim())}>
                  <span className="k-row__tile k-row__tile--brand" aria-hidden="true">
                    {I.plus}
                  </span>
                  <span className="k-row__txt">
                    <span className="k-row__title">Not here? Add it as a custom food</span>
                  </span>
                  <span className="k-row__end" aria-hidden="true">
                    {I["chevron-right"]}
                  </span>
                </button>
              </div>
            ) : null}
          </div>
          <p className="k-qfine">Tap a family to keep every dish out, or one dish to keep just that out.</p>
        </>
      ) : (
        <>
          <p className="k-qfine">Never suggested; you can still log them.</p>
          {firstFamily ? (
            <p className="k-qfine">
              Added foods are kept as the dish family, so every {firstFamily.label.toLowerCase()} dish stays out.
            </p>
          ) : null}
          {ruled ? <p className="k-qfine">{ruled}</p> : null}
        </>
      )}
    </div>
  );
}

/* ---------- the read-back ---------- */

function ReadBack({
  sentences,
  changes,
  title = "Your answers",
  onClose,
  onSave,
}: {
  sentences: string[];
  changes: { targets: string; plan: string; today: string };
  title?: string;
  onClose: () => void;
  onSave: () => void;
}) {
  return (
    <Sheet
      large
      title={title}
      sub="Check them before Kaya uses them."
      onClose={onClose}
      footer={
        <ActionBar inSheet secondary={{ label: "Change an answer", onClick: onClose }} primary={{ label: "Save", onClick: onSave }} />
      }
    >
      <div className="k-q">
        <ul className="k-qback">
          {sentences.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
        <RowGroup heading="What changes">
          <NavRow kind="value" icon={I.target} title="Targets" subtitle={changes.targets} />
          <NavRow kind="value" icon={I.calendar} title="The plan" subtitle={changes.plan} />
          <NavRow kind="value" icon={I.sun} title="Today" subtitle={changes.today} />
        </RowGroup>
      </div>
    </Sheet>
  );
}
